import { Injectable, inject, signal } from '@angular/core';

import { CatalogueApiService, SearchCriteria } from '@angular-workspace/services';

import {
  CanvasDataset,
  CANVAS_OGC_QUERY_TYPES_SEARCH_CRITERION,
  CANVAS_PUBLISHED_SEARCH_CRITERION,
  DEFAULT_CANVAS_QUERY_TYPES,
  datasetMatchesCanvasQueryType,
  mapCatalogueResponseToCanvasDatasets,
} from './canvas-catalogue.util';

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_SORT = 'itemCreatedAt:desc';
const SEARCH_DEBOUNCE_MS = 300;

export interface CanvasDatasetFilters {
  organisations: string[];
  tags: string[];
  organizationTypes: string[];
  /** Canvas query types: FEATURE, STAC (resourceServer.queryTypes). */
  apiTypes: string[];
  accessPolicies: string[];
  crs: string[];
  themes: string[];
}

const EMPTY_FILTERS: CanvasDatasetFilters = {
  organisations: [],
  tags: [],
  organizationTypes: [],
  apiTypes: [],
  accessPolicies: [],
  crs: [],
  themes: [],
};

@Injectable({ providedIn: 'root' })
export class CanvasDatasetsService {
  private readonly catalogueApi = inject(CatalogueApiService);

  readonly datasets = signal<CanvasDataset[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly searchQuery = signal('');

  private resourceServerUrls: string[] = [];
  private filters: CanvasDatasetFilters = { ...EMPTY_FILTERS };
  private searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private loadRequestId = 0;

  async loadOgcDatasets(resourceServerUrls: string[]): Promise<void> {
    const urls = [...new Set(resourceServerUrls.map((u) => u.trim()).filter(Boolean))];
    if (urls.length === 0) {
      return;
    }

    this.resourceServerUrls = urls;
    this.filters = { ...EMPTY_FILTERS };
    this.searchQuery.set('');
    await this.fetchDatasets('');
  }

  searchDatasets(query: string): void {
    this.searchQuery.set(query);

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    const trimmed = query.trim();
    if (trimmed.length > 0 && trimmed.length < 3) {
      return;
    }

    this.searchDebounceTimer = setTimeout(() => {
      void this.fetchDatasets(trimmed);
    }, SEARCH_DEBOUNCE_MS);
  }

  applyFilters(filters: CanvasDatasetFilters): void {
    this.filters = {
      organisations: [...filters.organisations],
      tags: [...filters.tags],
      organizationTypes: [...filters.organizationTypes],
      apiTypes: [...filters.apiTypes],
      accessPolicies: [...filters.accessPolicies],
      crs: [...filters.crs],
      themes: [...filters.themes],
    };
    void this.fetchDatasets(this.searchQuery().trim());
  }

  private buildResourceServerCriteria(resourceServerUrls: string[]): SearchCriteria[] {
    if (resourceServerUrls.length === 0) {
      return [];
    }

    return [
      {
        searchType: 'flattenedTerm',
        field: 'resourceServer.url',
        values: resourceServerUrls,
      },
    ];
  }

  private buildFilterCriteria(filters: CanvasDatasetFilters): SearchCriteria[] {
    const criteria: SearchCriteria[] = [];

    if (filters.organisations.length > 0) {
      criteria.push({
        searchType: 'term',
        field: 'organization',
        values: filters.organisations,
      });
    }

    if (filters.tags.length > 0) {
      criteria.push({
        searchType: 'term',
        field: 'tags',
        values: filters.tags,
      });
    }

    if (filters.organizationTypes.length > 0) {
      criteria.push({
        searchType: 'term',
        field: 'organizationType',
        values: filters.organizationTypes,
      });
    }

    const queryTypes =
      filters.apiTypes.length > 0
        ? filters.apiTypes
        : [...DEFAULT_CANVAS_QUERY_TYPES];
    criteria.push({
      ...CANVAS_OGC_QUERY_TYPES_SEARCH_CRITERION,
      values: queryTypes,
    });

    if (filters.accessPolicies.length > 0) {
      criteria.push({
        searchType: 'term',
        field: 'accessPolicy',
        values: filters.accessPolicies,
      });
    }

    if (filters.themes.length > 0) {
      criteria.push({
        searchType: 'term',
        field: 'concepts',
        values: filters.themes,
      });
    }

    if (filters.crs.length > 0) {
      criteria.push({
        searchType: 'term',
        field: 'crs',
        values: filters.crs,
      });
    }

    return criteria;
  }

  private async fetchDatasets(query: string): Promise<void> {
    if (this.resourceServerUrls.length === 0) {
      return;
    }

    const requestId = ++this.loadRequestId;
    this.loading.set(true);
    this.error.set(null);

    try {
      const searchCriteria = [
        CANVAS_PUBLISHED_SEARCH_CRITERION,
        ...this.buildResourceServerCriteria(this.resourceServerUrls),
        ...this.buildFilterCriteria(this.filters),
      ];

      const response = await this.catalogueApi.searchAllAssets(
        query,
        ['Dataset'],
        [],
        1,
        DEFAULT_PAGE_SIZE,
        DEFAULT_SORT,
        searchCriteria,
      );

      if (requestId !== this.loadRequestId) {
        return;
      }

      let datasets = mapCatalogueResponseToCanvasDatasets(response);
      const queryTypeFilter =
        this.filters.apiTypes.length > 0
          ? this.filters.apiTypes
          : [...DEFAULT_CANVAS_QUERY_TYPES];
      datasets = datasets.filter((d) =>
        queryTypeFilter.some((t) => datasetMatchesCanvasQueryType(d, t)),
      );
      this.datasets.set(datasets);
    } catch {
      if (requestId !== this.loadRequestId) {
        return;
      }

      this.error.set('Failed to load datasets.');
      this.datasets.set([]);
    } finally {
      if (requestId === this.loadRequestId) {
        this.loading.set(false);
      }
    }
  }
}
